import classNames from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { useUsers } from "../../api/users";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useDebounce } from "../../hooks/useDebounce";
import { useTranslation } from "../../hooks/useTranslation";
import type { UserLightResponse } from "../../types/user";
import styles from "./UserSingleSelect.module.css";

interface UserSingleSelectProps {
  selected?: UserLightResponse | null;
  onChange: (user: UserLightResponse | null) => void;
  excludeIds?: string[];
}

export function UserSingleSelect({ selected = null, onChange, excludeIds = [] }: UserSingleSelectProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [dropUp, setDropUp] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useClickOutside(rootRef, () => setIsOpen(false));

  const { data: usersPage, isLoading } = useUsers({ search: debouncedSearch }, debouncedSearch.length > 0);
  const users = usersPage?.data ?? [];

  useLayoutEffect(() => {
    if (!inputWrapperRef.current || !dropdownRef.current) return;
    const inputRect = inputWrapperRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    setDropUp(window.innerHeight - inputRect.bottom < dropdownHeight);
  }, [debouncedSearch, isLoading]);

  const filtered = users.filter((u) => !excludeIds.includes(u.id));

  function select(user: UserLightResponse) {
    onChange(user);
    setSearch("");
    setIsOpen(false);
  }

  function clear() {
    onChange(null);
    setSearch("");
    setIsOpen(false);
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.inputWrapper} ref={inputWrapperRef}>
        {selected ? (
          <span className={styles.selectedChip}>
            {selected.firstName} {selected.lastName}
            <button
              type="button"
              onClick={clear}
              className={styles.removeButton}
              aria-label={`Remove ${selected.firstName} ${selected.lastName}`}
            >
              &times;
            </button>
          </span>
        ) : (
          <input
            type="text"
            placeholder={t("common.search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            className={styles.searchInput}
          />
        )}
        {isOpen && !selected && (isLoading || debouncedSearch.length > 0) && (
          <div ref={dropdownRef} className={classNames(styles.dropdown, dropUp && styles.dropdownAbove)}>
            {isLoading ? (
              <div className={styles.loadingText}>{t("common.loading")}</div>
            ) : (
              <ul className={styles.results}>
                {filtered.length === 0 && <li className={styles.emptyText}>{t("common.noData")}</li>}
                {filtered.map((u) => (
                  <li key={u.id} className={styles.resultItem}>
                    <button
                      type="button"
                      onClick={() => select(u)}
                      className={styles.resultButton}
                    >
                      <span className={styles.resultName}>
                        {u.firstName} {u.lastName}
                        <span className={styles.resultEmail}>({u.email})</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
